import { FC, useMemo, useState } from 'react'
import { useT } from '_metronic/i18n/Metronici18n'
import { Button, CheckBox, CustomSelect, IOption, Input } from '../../../../../../components'
import { ButtonType } from '../../../../../../../_metronic/helpers'
import { CampoResolvido, FormularioCampoView } from '../../core/_models'
import { FormularioPreenchimento } from '../../../preenchimento/FormularioPreenchimento'
import {
  CATALOGO_PADRAO,
  CondicaoCatalogo,
  variavelPorCodigo,
} from '../../../core/condicao/catalogo'
import { variaveisUsadas } from '../../../core/condicao/variaveisUsadas'

export type IProps = {
  campos: FormularioCampoView[]
  catalogo?: CondicaoCatalogo
}

/** Campo ainda sem código (formulário novo): recebe uma chave local só para a pré-visualização. */
const codigoLocal = (campo: FormularioCampoView): string =>
  campo.codigo || `id-${campo.id ?? campo.ordem}`

const paraResolvido = (campo: FormularioCampoView): CampoResolvido => ({
  id: campo.id,
  codigo: codigoLocal(campo),
  ordem: campo.ordem,
  rotulo: campo.rotulo,
  instrucoes: campo.instrucoes,
  respostaPadrao: campo.respostaPadrao,
  tipo: campo.tipo,
  obrigatorio: campo.obrigatorio,
  multiplo: campo.multiplo,
  opcoes: (campo.opcoes ?? []).map((opcao, indice) => ({
    id: opcao.id,
    ordem: opcao.ordem,
    codigo: opcao.codigo || `opcao-${opcao.ordem ?? indice + 1}`,
    descricao: opcao.descricao,
  })),
  condicaoExibicao: campo.condicaoExibicao ?? null,
})

/**
 * Pré-visualização do formulário: mostra e esconde campos em tempo real conforme as respostas e o
 * painel "Simular dados do paciente". Não grava nada e não envia nada.
 */
export const FormularioPreview: FC<IProps> = ({ campos, catalogo = CATALOGO_PADRAO }) => {
  const { t } = useT()

  const resolvidos = useMemo(() => campos.map(paraResolvido), [campos])
  const variaveis = useMemo(() => variaveisUsadas(resolvidos), [resolvidos])

  const [simulacao, setSimulacao] = useState<Record<string, string>>({})

  const definir = (codigo: string, valor: string | null) =>
    setSimulacao((anterior) => {
      const proximo = { ...anterior }
      if (valor === null) delete proximo[codigo]
      else proximo[codigo] = valor
      return proximo
    })

  const valoresSimulados = useMemo(() => {
    const mapa: Record<string, string | number> = {}
    Object.entries(simulacao).forEach(([codigo, valor]) => {
      const variavel = variavelPorCodigo(catalogo, codigo)
      if (variavel?.tipo === 'NUMERO') {
        const numero = Number(valor)
        if (Number.isFinite(numero) && valor !== '') mapa[codigo] = numero
        return
      }
      if (valor !== '') mapa[codigo] = valor
    })
    return mapa
  }, [simulacao, catalogo])

  return (
    <div>
      <fieldset className='formulario-simulacao mb-4'>
        <legend className='fs-6 fw-bolder'>{t('formularios.preenchimento.simular.titulo')}</legend>
        {variaveis.length === 0 ? (
          <p className='text-muted fs-8 mb-0'>{t('formularios.preenchimento.simular.vazio')}</p>
        ) : (
          variaveis.map((codigo) => {
            const variavel = variavelPorCodigo(catalogo, codigo)
            const semDado = simulacao[codigo] === undefined
            const idSemDado = `simular-${codigo}-sem-dado`
            const idValor = `simular-${codigo}-valor`
            const opcoesValor: IOption<string>[] = (variavel?.valoresPermitidos ?? []).map(
              (permitido) => ({ value: permitido.valor, label: permitido.rotulo })
            )
            return (
              <div key={codigo} className='formulario-simulacao__linha'>
                <span className='fs-7 fw-semibold'>{variavel?.rotulo ?? codigo}</span>
                <CheckBox
                  id={idSemDado}
                  label={t('formularios.preenchimento.simular.semDado')}
                  checked={semDado}
                  onChange={() => definir(codigo, semDado ? '' : null)}
                />
                {!semDado &&
                  (opcoesValor.length > 0 ? (
                    <CustomSelect
                      inputId={idValor}
                      className='formulario-simulacao__valor'
                      ariaLabel={variavel?.rotulo ?? codigo}
                      placeholder={t('formularios.condicao.ref.placeholder')}
                      options={opcoesValor}
                      value={opcoesValor.find((opcao) => opcao.value === simulacao[codigo])}
                      onChange={(opcao: IOption<string> | null) =>
                        definir(codigo, opcao?.value ?? '')
                      }
                    />
                  ) : (
                    <div className='formulario-simulacao__valor'>
                      <Input
                        id={idValor}
                        type='number'
                        aria-label={variavel?.rotulo ?? codigo}
                        placeholder=''
                        value={simulacao[codigo] ?? ''}
                        onChange={(e) => definir(codigo, e.target.value)}
                      />
                    </div>
                  ))}
              </div>
            )
          })
        )}
      </fieldset>

      <FormularioPreenchimento
        campos={resolvidos}
        variaveis={valoresSimulados}
        renderAcoes={() => (
          <div className='d-flex justify-content-end'>
            <Button type={ButtonType.Submit} className='btn-primary' disabled>
              {t('formularios.preenchimento.preview.enviar')}
            </Button>
          </div>
        )}
      />
    </div>
  )
}
